/**
 * @fileoverview SkeletonText — one or more text-line shimmer lines.
 * @package @puffin-core/skeleton-kit
 */

import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';

/**
 * @typedef {object} SkeletonTextProps
 * @property {number} [lines=1]
 * @property {string|false} [lastLineWidth='70%'] - The last line's CSS width; `false` leaves it
 *   full width.
 * @property {boolean} [animated=true]
 * @property {string} [className]
 */

/**
 * Memoised; it keeps its `propTypes` declaration until JSDOC-PROP-CONTRACT-001 Phase 3 retires it.
 *
 * @type {React.NamedExoticComponent<SkeletonTextProps> & {propTypes?: object}}
 */
export const SkeletonText = memo(
  /**
   * @param {SkeletonTextProps} props
   * @returns {React.JSX.Element}
   */
  function SkeletonText({ lines = 1, lastLineWidth = '70%', animated = true, className = '' }) {
    const lineCount = useMemo(() => {
      if (typeof lines !== 'number' || Number.isNaN(lines)) return 1;
      if (lines < 0) return 1;
      return Math.floor(lines);
    }, [lines]);

    const renderLines = useMemo(() => {
      if (lineCount === 0) return [];
      const baseClass = [
        'skeleton',
        'skeleton-text',
        animated ? null : 'skeleton-static',
        className,
      ]
        .filter(Boolean)
        .join(' ')
        .trim();
      return Array.from({ length: lineCount }, (_, index) => {
        const isLast = index === lineCount - 1 && lineCount > 1;
        const lineStyle = isLast && lastLineWidth ? { width: lastLineWidth } : undefined;
        return <div key={index} className={baseClass} aria-hidden="true" style={lineStyle} />;
      });
    }, [lineCount, lastLineWidth, animated, className]);

    if (lineCount === 0) return null;
    return <>{renderLines}</>;
  },
);

SkeletonText.propTypes = {
  lines: PropTypes.number,
  lastLineWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  animated: PropTypes.bool,
  className: PropTypes.string,
};

export default SkeletonText;
